// D2 — Web Documentation: secure Markdown renderer (Master §31, TechSpec §14).
//
// SECURITY MODEL (do not "simplify" — this is the docs XSS surface):
//  * react-markdown is used WITHOUT rehype-raw, so any raw HTML in the source
//    (`<script>`, `<iframe>`, `<object>`, inline `<img>`, event-handler attrs)
//    is NOT parsed as HTML — it is emitted as inert escaped text. No script can
//    execute and no unsafe embed can be injected via raw markup.
//  * Markdown *links* are still real anchors, so their `href` is sanitized:
//      - http(s)  -> external, forced target="_blank" + rel="noopener noreferrer"
//      - mailto:  -> allowed
//      - internal (relative doc path) -> resolved to a canonical slug and
//        rendered as an in-app deep link (no full navigation)
//      - javascript:/data:/vbscript:/protocol-relative/anything else -> the
//        link is DOWNGRADED to inert text (never an executable href)
//  * Markdown *images* `![]()` are sanitized to http(s) only (data:/javascript:
//    blocked); a referrerpolicy is added. No other embed is possible because
//    raw HTML is inert (above).
//
// The single shared ChatThread renderer (MarkdownBlock) is left untouched —
// this is the Documentation-specific pipeline that adds link sanitization and
// in-app internal-link routing on top of the same react-markdown core.

import { type ComponentType, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { CodeBlock } from "@/components/MarkdownBlock";
import { docsArticleHash } from "@/lib/docs/deepLink";

// ---- URL sanitization ----
const SAFE_EXTERNAL = /^https?:\/\//i;
const SAFE_MAILTO = /^mailto:/i;
// Anything with a URL scheme (http:, https:, mailto:, javascript:, data:,
// vbscript:, file:, ...) — we re-decide per-scheme below.
const HAS_SCHEME = /^[a-z][a-z0-9+.\-]*:/i;

export type SafeHref =
  | { kind: "external"; href: string }
  | { kind: "mailto"; href: string }
  | { kind: "blocked" };

/** Decide how a raw markdown href should be rendered (links only). */
export function sanitizeHref(raw: string | undefined | null): SafeHref {
  if (!raw) return { kind: "blocked" };
  const url = raw.trim();
  // Collapse internal whitespace (a classic javascript: obfuscation trick).
  const compact = url.replace(/[\s]+/g, "");
  if (SAFE_EXTERNAL.test(compact)) return { kind: "external", href: url };
  if (SAFE_MAILTO.test(compact)) return { kind: "mailto", href: url };
  // protocol-relative (//host), data:, javascript:, vbscript:, file:, and any
  // other scheme are all blocked.
  if (url.startsWith("//") || HAS_SCHEME.test(compact)) return { kind: "blocked" };
  // A bare scheme-less token is a *relative doc path* (handled by the
  // internal-link resolver, not here) — signal "blocked" so the anchor does
  // not emit a raw relative href; the resolver upgrades it to an in-app link.
  return { kind: "blocked" };
}

export function isSafeImageSrc(src: string | undefined | null): boolean {
  if (!src) return false;
  const compact = src.trim().replace(/[\s]+/g, "");
  return SAFE_EXTERNAL.test(compact);
}

/**
 * Resolve a RELATIVE internal doc link (from the current article) to a
 * canonical slug, or null when the target is not a relative doc path.
 *
 *   base "user/chat/overview" + "new-task.md"        -> "user/chat/new-task"
 *   base "user/chat/overview" + "../models/overview.md" -> "user/models/overview"
 *   base "user/chat/overview" + "overview.md#x"      -> "user/chat/overview"
 */
export function resolveDocSlug(baseSlug: string, href: string | undefined | null): string | null {
  if (!href) return null;
  const url = href.trim();
  // strip an in-page anchor
  const hashIdx = url.indexOf("#");
  const pathPart = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  if (!pathPart) return null; // pure in-page anchor (#heading) — not an article link
  if (url.startsWith("//") || HAS_SCHEME.test(pathPart)) return null; // external / scheme
  if (pathPart.startsWith("/")) return null; // absolute path, not a relative doc path

  const p = pathPart.endsWith(".md") ? pathPart.slice(0, -3) : pathPart;
  const lastSlash = baseSlug.lastIndexOf("/");
  const baseDir = lastSlash >= 0 ? baseSlug.slice(0, lastSlash + 1) : "";
  const combined = baseDir + p;

  const stack: string[] = [];
  for (const seg of combined.split("/")) {
    if (!seg || seg === ".") continue;
    if (seg === "..") stack.pop();
    else stack.push(seg);
  }
  return stack.join("/");
}

interface LinkCtx {
  baseSlug: string;
  onNavigate: (slug: string) => void;
  articleExists: (slug: string) => boolean;
}

function DocLink({
  href,
  children,
  ctx,
}: {
  href?: string;
  children?: ReactNode;
  ctx: LinkCtx;
}) {
  // 1) Internal relative doc link (the normal case in the corpus).
  const internalSlug = resolveDocSlug(ctx.baseSlug, href);
  if (internalSlug && ctx.articleExists(internalSlug)) {
    return (
      <a
        href={docsArticleHash(internalSlug)}
        onClick={(e) => {
          e.preventDefault();
          ctx.onNavigate(internalSlug);
        }}
        className="text-accent underline decoration-accent/50 underline-offset-2 hover:decoration-accent transition-colors"
      >
        {children}
      </a>
    );
  }
  // 2) External / mailto — sanitized.
  const safe = sanitizeHref(href);
  if (safe.kind === "external") {
    return (
      <a
        href={safe.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent underline decoration-accent/50 underline-offset-2 hover:decoration-accent transition-colors"
      >
        {children}
      </a>
    );
  }
  if (safe.kind === "mailto") {
    return (
      <a href={safe.href} className="text-accent underline decoration-accent/50 underline-offset-2 hover:decoration-accent transition-colors">
        {children}
      </a>
    );
  }
  // 3) Blocked (javascript:/data:/unknown) — inert text, never an executable href.
  return <span className="text-secondary">{children}</span>;
}

interface ImgProps {
  src?: string;
  alt?: string;
  children?: ReactNode;
}

function DocImage({ src, alt }: ImgProps) {
  if (!isSafeImageSrc(src)) {
    // Unsafe image source (data:/javascript:/etc.) — do not render.
    return alt ? <span className="text-secondary">({alt})</span> : null;
  }
  return (
    <img
      src={src}
      alt={alt ?? ""}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="my-3 max-w-full rounded-lg border border-strongline"
    />
  );
}

/**
 * Documentation Markdown renderer.
 *
 * @param content    article body (frontmatter stripped)
 * @param baseSlug   current article slug — used to resolve relative internal links
 * @param onNavigate in-app navigation to a canonical article slug
 * @param articleExists  predicate over the loaded corpus (guards internal links)
 */
export function DocsMarkdown({
  content,
  baseSlug,
  onNavigate,
  articleExists,
}: {
  content: string;
  baseSlug: string;
  onNavigate: (slug: string) => void;
  articleExists: (slug: string) => boolean;
}) {
  const ctx: LinkCtx = { baseSlug, onNavigate, articleExists };

  const components: Record<string, ComponentType<{ children?: ReactNode } & Record<string, unknown>>> = {
    a: (props) => <DocLink href={props.href as string | undefined} children={props.children} ctx={ctx} />,
    img: (props) => (
      <DocImage src={props.src as string | undefined} alt={props.alt as string | undefined} />
    ),
    pre: CodeBlock as unknown as ComponentType<{ children?: ReactNode } & Record<string, unknown>>,
    // Table wrapper: horizontal scroll on narrow viewports (mobile-web).
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border border-strongline bg-elevated px-3 py-1.5 text-left font-medium text-primary">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-strongline px-3 py-1.5 text-secondary align-top">{children}</td>
    ),
    h1: ({ children }) => <h2 className="mt-8 mb-3 text-2xl font-medium text-primary scroll-mt-20">{children}</h2>,
    h2: ({ children }) => <h3 className="mt-7 mb-2.5 text-xl font-medium text-primary scroll-mt-20">{children}</h3>,
    h3: ({ children }) => <h4 className="mt-6 mb-2 text-lg font-medium text-primary scroll-mt-20">{children}</h4>,
    h4: ({ children }) => <h5 className="mt-5 mb-2 text-base font-medium text-primary scroll-mt-20">{children}</h5>,
    h5: ({ children }) => <h6 className="mt-4 mb-2 text-base font-medium text-primary scroll-mt-20">{children}</h6>,
    h6: ({ children }) => <h6 className="mt-4 mb-2 text-sm font-medium text-secondary scroll-mt-20">{children}</h6>,
    p: ({ children }) => <p className="my-3 leading-relaxed text-primary">{children}</p>,
    ul: ({ children }) => <ul className="my-3 list-disc space-y-1.5 pl-5 text-primary">{children}</ul>,
    ol: ({ children }) => <ol className="my-3 list-decimal space-y-1.5 pl-5 text-primary">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="my-4 border-l-2 border-accent/60 pl-4 text-secondary italic">{children}</blockquote>
    ),
    code: ({ children }) => (
      <code className="rounded bg-elevated px-1.5 py-0.5 font-mono text-[0.85em] text-primary">{children}</code>
    ),
    hr: () => <hr className="my-6 border-hairline" />,
    strong: ({ children }) => <strong className="font-semibold text-primary">{children}</strong>,
    em: ({ children }) => <em className="text-primary">{children}</em>,
  };

  return (
    <div className="docs-prose max-w-none">
      <ReactMarkdown components={components as never}>{content || ""}</ReactMarkdown>
    </div>
  );
}
